import type { ViewResponse } from "@redotech/effect-api-shared/rpc/view-folder";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  buildCustomSupportTicketCountViews,
  buildDefaultSupportTicketCountViews,
  type SupportTicketCountSourceView,
} from "@redotech/redo-support-v2/query/support-ticket-count-query";
import { supportTicketCountsScopedQueryKey } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { catchApiErrorsSilent } from "../db/error-handling";
import { useSupportTicketQueryScope } from "./use-support-ticket-query-scope";

export function useSupportTicketCounts(
  views: ViewResponse[],
): Map<string, number> {
  const user = useRequiredContext(UserContext);
  const userId = user._id.toString();
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryScope = useSupportTicketQueryScope();

  const viewsForCounts = useMemo(() => {
    return [
      ...buildDefaultSupportTicketCountViews(userId),
      ...buildCustomSupportTicketCountViews(
        views as readonly SupportTicketCountSourceView[],
      ),
    ];
  }, [views, userId]);

  const { data } = useQuery({
    queryKey: [
      ...supportTicketCountsScopedQueryKey(queryScope),
      viewsForCounts.map((v) => v.viewKey),
    ],
    queryFn: async () => {
      const client = effectApi.getClient();
      const result = await effectApi.runPromise(
        catchApiErrorsSilent(
          client.supportTicketCounts.getSupportTicketCounts({
            payload: { views: viewsForCounts },
          }),
        ),
      );
      return result as Record<string, number>;
    },
    enabled: viewsForCounts.length > 0,
    staleTime: 0,
    meta: { showErrorToast: true },
  });

  return useMemo(() => {
    const map = new Map<string, number>();
    if (data) {
      for (const [key, count] of Object.entries(data)) {
        map.set(key, count);
      }
    }
    return map;
  }, [data]);
}
